// Resolver for the merchant-configurable per-field `required` flag +
// inline helper text on every built-in input the form renders.
//
// Both the hosted /request page and the embed bundle import this
// module — pure helpers, no Postgres / Redis deps. The embed
// bundle gets it via the public /api/shop-config response (the
// `builtin_fields` field is allow-listed in PublicShopConfig).
//
// Defaults preserve today's behavior on shops that haven't touched
// the admin card. Anyone explicitly setting a field's required:
// false flag flips the asterisk off + skips its validation; an
// empty helper string just renders nothing.
import type { BuiltinFieldKey, BuiltinFieldConfig } from "@/lib/shop-config-types";

export type FieldId = BuiltinFieldKey;

/** Today's behavior frozen as the per-field default. New shops
 *  inherit these; merchants opt into looser requirements via
 *  /settings/forms. */
export const BUILTIN_FIELD_DEFAULTS: Record<FieldId, { required: boolean }> = {
  first_name:        { required: true },
  last_name:         { required: true },
  email:             { required: true },
  phone:             { required: true },
  company:           { required: false },
  preferred_contact: { required: false },
  address:           { required: true },
  street_address_2:  { required: false },
  city:              { required: true },
  country:           { required: true },
  state:             { required: true },
  zip:               { required: true },
};

/** Resolve a field's effective settings. Always returns a shape so
 *  callers can render unconditionally — `helper` is empty string
 *  when not set, `required` falls back to the default map. */
export function resolveBuiltinField(
  key: FieldId,
  config: { builtin_fields?: Partial<Record<FieldId, BuiltinFieldConfig>> } | undefined,
): { required: boolean; helper: string } {
  const override = config?.builtin_fields?.[key];
  return {
    required: typeof override?.required === "boolean"
      ? override.required
      : BUILTIN_FIELD_DEFAULTS[key].required,
    helper: (override?.helper || "").toString(),
  };
}

/** Convenience: returns the asterisk suffix (or "") to append to a
 *  field label based on whether the field is required. Mirrors the
 *  current "First name *" pattern. */
export function asteriskFor(
  key: FieldId,
  config: { builtin_fields?: Partial<Record<FieldId, BuiltinFieldConfig>> } | undefined,
): string {
  return resolveBuiltinField(key, config).required ? " *" : "";
}

/** Ordered list of the keys + a human label for each. Used by the
 *  admin editor to render the rows in a stable order. */
export const BUILTIN_FIELD_ROWS: { key: FieldId; label: string; section: "about" | "shipping" }[] = [
  { key: "first_name",        label: "First name",        section: "about" },
  { key: "last_name",         label: "Last name",         section: "about" },
  { key: "email",             label: "Email",             section: "about" },
  { key: "phone",             label: "Phone",             section: "about" },
  { key: "company",           label: "Company",           section: "about" },
  { key: "preferred_contact", label: "Preferred contact", section: "about" },
  { key: "address",           label: "Street address",    section: "shipping" },
  { key: "street_address_2",  label: "Apt / Suite / Unit", section: "shipping" },
  { key: "city",              label: "City",              section: "shipping" },
  { key: "state",             label: "State / Region",    section: "shipping" },
  { key: "zip",               label: "ZIP",               section: "shipping" },
  { key: "country",           label: "Country",           section: "shipping" },
];

/** Resolve the effective step ("about" | "shipping" | "finish") for a
 *  built-in field. Shipping defaults stay anchored to "shipping" — the
 *  section-level Require shipping toggle is the only way to hide
 *  them — so step overrides for those keys are clamped back to their
 *  default. About / Finish keys can move freely. */
export function resolveBuiltinStep(
  key: FieldId,
  config: { builtin_fields?: Partial<Record<FieldId, BuiltinFieldConfig>> } | undefined,
): "about" | "shipping" | "finish" {
  const defaultRow = BUILTIN_FIELD_ROWS.find(r => r.key === key);
  const defaultStep = (defaultRow?.section as "about" | "shipping") || "about";
  // Shipping fields are gated by the section toggle, not per-field —
  // ignore step overrides for them so a stale override can't leak a
  // shipping field into another step.
  if (defaultStep === "shipping") return "shipping";
  const override = config?.builtin_fields?.[key]?.step;
  if (override === "about" || override === "finish") return override;
  return defaultStep;
}

/** Merchant-overridable label, falling back to BUILTIN_FIELD_ROWS. */
export function resolveBuiltinLabel(
  key: FieldId,
  config: { builtin_fields?: Partial<Record<FieldId, BuiltinFieldConfig>> } | undefined,
): string {
  const override = config?.builtin_fields?.[key]?.label;
  if (typeof override === "string" && override.trim().length > 0) return override;
  return BUILTIN_FIELD_ROWS.find(r => r.key === key)?.label || key;
}

/** Default column_width per built-in. first/last/city/state/zip pack
 *  into pairs (half); everything else fills the row (full). A merchant
 *  override on builtin_fields[key].column_width always wins. */
const BUILTIN_DEFAULT_COLUMN_WIDTH: Record<FieldId, "full" | "half" | "third"> = {
  first_name: "half",
  last_name:  "half",
  email:      "full",
  phone:      "full",
  company:    "full",
  preferred_contact: "full",
  address:    "full",
  street_address_2: "full",
  city:       "half",
  country:    "half",
  state:      "half",
  zip:        "half",
};

export function resolveBuiltinColumnWidth(
  key: FieldId,
  config: { builtin_fields?: Partial<Record<FieldId, BuiltinFieldConfig>> } | undefined,
): "full" | "half" | "third" {
  const override = config?.builtin_fields?.[key]?.column_width;
  if (override === "half" || override === "full" || override === "third") return override;
  return BUILTIN_DEFAULT_COLUMN_WIDTH[key];
}

/** Default display_order falls back to the field's index in
 *  BUILTIN_FIELD_ROWS so the visual order matches the legacy form. */
export function resolveBuiltinOrder(
  key: FieldId,
  config: { builtin_fields?: Partial<Record<FieldId, BuiltinFieldConfig>> } | undefined,
): number {
  const override = config?.builtin_fields?.[key]?.display_order;
  if (typeof override === "number") return override;
  return BUILTIN_FIELD_ROWS.findIndex(r => r.key === key);
}
